import { z } from 'zod';

export const FileSchema = z.object({
  label: z.string(),
  isPdfFile: z.boolean().optional(),
  noOfPages: z.number().optional(),
  isProcessing: z.boolean().optional(),

  fileName: z.string(),
  fileSize: z.number().optional(), // KB size
  fileType: z.string().optional(),
  s3Key: z.string().optional(),
});

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export interface IFile extends z.infer<typeof FileSchema> {}

export enum DocAccessType {
  STAFF_ONLY = 'STAFF_ONLY',
  AGENT_ONLY = 'AGENT_ONLY',
}

export enum DocStatus {
  AWAITING = 'AWAITING',
  UPLOADED_OK = 'UPLOADED_OK',
  UPLOADED_WITH_ERROR = 'UPLOADED_WITH_ERROR',
  REJECTED = 'REJECTED',
  REJECTED_WITHOUT_ALTERNATIVE = 'REJECTED_WITHOUT_ALTERNATIVE',
  APPROVED = 'APPROVED',
  REPLACED = 'REPLACED',
  DELETED = 'DELETED',
  SKIPPED = 'SKIPPED',
  INFO_ONLY = 'INFO_ONLY',
  NOT_ACKNOWLEDGED = 'NOT_ACKNOWLEDGED',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
}

export enum UploadedBy {
  ADMIN = 'Admin',
  AGENT = 'Agent',
  UNIVERSITY = 'University',
  ENROLY = 'Enroly',
  SITS = 'SITS',
}

export const DocumentStatusSchema = z.nativeEnum(DocStatus);

export const UploadedBySchema = z.union([
  z.literal(''),
  z.literal(UploadedBy.ADMIN),
  z.literal(UploadedBy.AGENT),
  z.literal(UploadedBy.UNIVERSITY),
  z.literal(UploadedBy.ENROLY),
  z.literal(UploadedBy.SITS),
]);

export type UploadedByType = z.infer<typeof UploadedBySchema>;

export const DocumentSchema = z.object({
  label: z.string(),
  status: DocumentStatusSchema,
  prevStatus: DocumentStatusSchema.optional(),
  fileNames: z.array(z.string()), // refactor this out after STM-392
  files: z.array(FileSchema).optional(),
  reason: z.string().optional(),
  signedUrls: z.array(z.string()).optional(),
  uploadedBy: UploadedBySchema.optional(),
  userAccess: z.nativeEnum(DocAccessType).optional(),
  isProcessingPdf: z.boolean().optional(),
  eTags: z.array(z.string()).optional(), // eTags from s3 fro faster lookups
  s3Keys: z.array(z.string()).optional(),
  fileLabels: z.array(z.string()).optional(), // Original user-facing filenames (fileNames may contain S3-mangled names)

  /** If a document is a stub after TTL deletion, it should have `deletedByTTL = true`  */
  deletedByTTL: z.boolean().optional(),
});
/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export interface IDocument extends z.infer<typeof DocumentSchema> {}

export enum TypeOfInterviewUpdate {
  BULK = 'BULK',
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
}

export interface IFixableDoc extends IDocument {
  isDocumentFixed?: boolean;
}

export interface DocumentMap {
  [key: string]: IDocument;
}

export interface BiometricDocument extends IDocument {
  hasDocument?: boolean;
  dateOfIssue?: string;
  validUntil?: string;
}
export interface BiometricDocumentMap {
  [key: string]: BiometricDocument;
}

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

// DeepReadonly start
// from https://github.com/ts-essentials/ts-essentials/blob/master/lib/types.ts
export type Primitive = string | number | boolean | bigint | symbol | undefined | null;
/* eslint-disable-next-line @typescript-eslint/no-unsafe-function-type */
export type Builtin = Primitive | Function | Date | Error | RegExp;
export type AnyArray<T = unknown> = Array<T> | ReadonlyArray<T>;
export type IsTuple<T> = T extends unknown[] ? (unknown[] extends T ? never : T) : never;
export type IsAny<T> = 0 extends 1 & T ? true : false;
export type IsUnknown<T> = IsAny<T> extends true ? false : unknown extends T ? true : false;

export type None = null | undefined;

export type Nullable<T> = T | None;

// Set all properties of T to be their type (K) | null | undefined.
export type AllNullable<T> = {
  [K in keyof T]: T[K] | None;
};

/** Like Readonly but recursive */
export type DeepReadonly<T> = T extends Builtin
  ? T
  : T extends Map<infer K, infer V>
    ? ReadonlyMap<DeepReadonly<K>, DeepReadonly<V>>
    : T extends ReadonlyMap<infer K, infer V>
      ? ReadonlyMap<DeepReadonly<K>, DeepReadonly<V>>
      : T extends WeakMap<infer K, infer V>
        ? WeakMap<DeepReadonly<K>, DeepReadonly<V>>
        : T extends Set<infer U>
          ? ReadonlySet<DeepReadonly<U>>
          : T extends ReadonlySet<infer U>
            ? ReadonlySet<DeepReadonly<U>>
            : T extends WeakSet<infer U>
              ? WeakSet<DeepReadonly<U>>
              : T extends Promise<infer U>
                ? Promise<DeepReadonly<U>>
                : T extends AnyArray<infer U>
                  ? T extends IsTuple<T>
                    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
                    : ReadonlyArray<DeepReadonly<U>>
                  : T extends object
                    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
                    : IsUnknown<T> extends true
                      ? unknown
                      : Readonly<T>;

// DeepReadonly end

export type Writeable<T> = { -readonly [P in keyof T]: T[P] };

export type DeepWriteable<T> = { -readonly [P in keyof T]: DeepWriteable<T[P]> };

export type Only<T, U> = {
  [P in keyof T]: T[P];
} & {
  [P in keyof U]?: never;
};

export type Either<T, U> = Only<T, U> | Only<U, T>;

// usage ex. type BooleanKeysInRegionConfig = KeysWithValsOfType<IRegionConfig, boolean>;
export type KeysWithValsOfType<T, V> = keyof { [P in keyof T as T[P] extends V ? P : never]: P };

export type PartialOrFull<IsPartial, Definition> = IsPartial extends true ? Partial<Definition> : Definition;

export type StripIndexSignature<T> = {
  [P in keyof T as string extends P ? never : number extends P ? never : P]: T[P];
};
